import React from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  Link as MatLink,
  OutlinedInput,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { RebelbaseLogoIcon } from "@/assets";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import LanguagePicker from "@/components/Common/LanguagePicker/LanguagePicker";
import { useNavigate } from "react-router";
import { ResetPasswordFormData } from "./ResetPasswordForm";
import radii from "@/theme/radii";

const ResetPassword = ({
  onSubmit,
  formHandleSubmit,
  register,
  errors,
  language,
}: ResetPasswordFormData) => {
  const navigate = useNavigate();

  const { t } = useTranslation(["translation.ResetPassword"]);

  const isMobileView = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const baseUrl = language === "ESP" ? "/es" : "";

  const authLink = (event: React.SyntheticEvent) => {
    event?.preventDefault();
    navigate("/auth/login");
  };

  const signupLink = (event: React.SyntheticEvent) => {
    event?.preventDefault();
    navigate("/auth/sign-up");
  };

  return (
    <form onSubmit={formHandleSubmit(onSubmit)} noValidate>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            maxHeight: 581,
            width: { xs: 328, md: 572 },
            background: (theme) => theme?.palette?.common?.white,
            borderRadius: `${radii.lg}px`,

            padding: (theme: Theme) =>
              theme.spacing(5, isMobileView ? 2 : 4, 12, isMobileView ? 2 : 4),

            position: "relative"
          }}>
          {isMobileView && (
            <>
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 16,
                }}
              >
                <IconButton onClick={authLink}>
                  <ArrowBackIosNewIcon
                    sx={{
                      height: 20,
                      width: 20,
                      color: (theme: Theme) => theme?.palette?.primary?.main,
                      "&:hover": {
                        color: (theme: Theme) => theme?.palette?.primary?.main,
                      },
                    }}
                  />
                </IconButton>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 16,
                }}
              >
                <LanguagePicker withLabel={false} />
              </Box>
            </>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              pt: { xs: 8, md: 2 },
            }}
          >
            {!isMobileView && (
              <Box sx={{
                flex: 0
              }}>
                <MatLink
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={authLink}
                  color="primary"
                >
                  <IconButton>
                    <ArrowBackIosNewIcon
                      sx={{
                        height: 20,
                        width: 20,
                        color: (theme: Theme) => theme?.palette?.primary?.main,
                        "&:hover": {
                          color: (theme: Theme) =>
                            theme?.palette?.primary?.main,
                        },
                      }}
                    />
                  </IconButton>
                </MatLink>
              </Box>
            )}
            <MatLink
              href={`https://rebelbase.co${baseUrl}`}
              color="primary"
              sx={{
                flex: "1 1 100%",
                display: "flex",
                justifyContent: "center"
              }}>
              <RebelbaseLogoIcon
                sx={{
                  height: 30,
                  width: 206,
                  color: (theme: Theme) => theme?.palette?.primary?.main,
                  "&:hover": {
                    color: (theme: Theme) => theme?.palette?.primary?.main,
                  },
                }}
              />
            </MatLink>
            {!isMobileView && (
              <Box sx={{
                flex: 0
              }}>
                <LanguagePicker withLabel={false} />
              </Box>
            )}
          </Box>
          <Typography
            sx={{
              pt: 3,
              pb: 4,
              typography: { xs: "h3", md: "h1" },
            }}
            color="secondary"
          >
            {t("ResetYourPassword")}
          </Typography>

          <Box
            sx={{
              pt: 2,
              width: { xs: "auto", md: "100%" },
            }}
          >
            <FormControl variant="outlined" fullWidth>
              <InputLabel error={Boolean(errors?.password)} htmlFor="password">
                {t("Password")}
              </InputLabel>
              <OutlinedInput
                id="password"
                type="password"
                fullWidth
                {...register("password", {
                  required: "Required",
                })}
                label={t("Password")}
                error={Boolean(errors?.password)}
              />
              {errors?.password && (
                <FormHelperText
                  error={Boolean(errors?.password)}
                  id="outlined-password-helper-text"
                >
                  {errors?.password?.message as string}
                </FormHelperText>
              )}
            </FormControl>
          </Box>

          <Box
            sx={{
              pt: 2,
              width: { xs: "auto", md: "100%" },
            }}
          >
            <FormControl variant="outlined" fullWidth>
              <InputLabel
                error={Boolean(errors?.confirm_password)}
                htmlFor="confirm_password"
              >
                {t("ConfirmPassword")}
              </InputLabel>
              <OutlinedInput
                id="confirm_password"
                type="password"
                fullWidth
                {...register("confirm_password", {
                  required: "Required",
                })}
                label={t("ConfirmPassword")}
                error={Boolean(errors?.confirm_password)}
              />
              {errors?.confirm_password && (
                <FormHelperText
                  error={Boolean(errors?.confirm_password)}
                  id="outlined-confirm-password-helper-text"
                >
                  {errors?.confirm_password?.message as string}
                </FormHelperText>
              )}
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pt: 6,
              pb: 4,
              width: "100%"
            }}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="large"
            >
              {t("ResetButton")}
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%"
            }}>
            <MatLink
              underline="none"
              color="primary"
              onClick={authLink}
              sx={{
                pl: 1,
                "& :hover": {
                  color: (theme: Theme) => theme?.palette?.primary?.main,
                },
              }}
            >
              <Typography variant="body1" color="primary">
                {t("Login")}
              </Typography>
            </MatLink>
            <Typography align="left" variant="body1" color="secondary">
              {" "}
              {t("or")}{" "}
            </Typography>
            <MatLink
              underline="none"
              color="primary"
              onClick={signupLink}
              sx={{
                "& :hover": {
                  color: (theme: Theme) => theme?.palette?.primary?.main,
                },
              }}
            >
              <Typography variant="body1" color="primary">
                {t("Signup")}
              </Typography>
            </MatLink>
            <Typography align="left" variant="body1" color="secondary">
              {" "}
              {t("instead")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

export default React.memo(ResetPassword);
